import React from 'react';
import { RotateCcw, Trash2, Clock } from 'lucide-react';
import { RecoverableSession } from '../hooks/useBlankRecordingRecovery';
import './BlankRecordingRecoveryBanner.scss';

interface BlankRecordingRecoveryBannerProps {
  sessions: RecoverableSession[];
  isRecovering: boolean;
  recoveryProgress: { current: number; total: number; stage: string } | null;
  onRecover: (recordingId: string) => void;
  onDiscard: (recordingId: string) => void;
  onDiscardAll: () => void;
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'just now';
}

function SessionDetails({ session }: { session: RecoverableSession }) {
  return (
    <span className="session-meta">
      {session.estimatedSizeMB} MB
      {session.duration > 0 && ` · ~${formatDuration(session.duration)}`}
      {session.hasScreen && ' · Screen'}
      {session.hasWebcam && ' · Webcam'}
      {session.hasAudio && ' · Audio'}
    </span>
  );
}

export const BlankRecordingRecoveryBanner: React.FC<BlankRecordingRecoveryBannerProps> = ({
  sessions,
  isRecovering,
  recoveryProgress,
  onRecover,
  onDiscard,
  onDiscardAll,
}) => {
  if (sessions.length === 0) return null;

  if (isRecovering) {
    return (
      <div className="recovery-overlay">
        <div className="overlay-card">
          <div className="card-header">
            <div className="icon-wrapper primary">
              <RotateCcw size={20} />
            </div>
            <div className="header-text">
              <h3 className="title">Recovering Recording</h3>
              <p className="subtitle">Please wait while your recording is being restored</p>
            </div>
          </div>
          <div className="card-body">
            <div className="spinner-container">
              <div className="spinner" />
            </div>
            <div className="progress-info">
              <p className="progress-text">
                {recoveryProgress?.stage || 'Preparing recovery...'}
              </p>
              <p className="progress-hint">This may take a moment depending on your connection</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recovery-overlay">
      <div className="overlay-card">
        <div className="card-header">
          <div className="icon-wrapper primary">
            <Clock size={20} />
          </div>
          <div className="header-text">
            <h3 className="title">Interrupted Recording Found</h3>
            <p className="subtitle">
              {sessions.length === 1
                ? 'A previous recording was interrupted and can be recovered'
                : `${sessions.length} interrupted recordings found`}
            </p>
          </div>
        </div>
        <div className="card-body">
          <div className="session-list">
            {sessions.map((session) => (
              <div className="session-row" key={session.recordingId}>
                <div className="session-info">
                  <p className="session-time">{formatTimeAgo(session.createdAt)}</p>
                  <p className="session-detail">
                    <SessionDetails session={session} />
                  </p>
                </div>
                <div className="session-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => onRecover(session.recordingId)}
                  >
                    <RotateCcw size={14} />
                    Recover
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => onDiscard(session.recordingId)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {sessions.length > 1 && (
            <button className="btn btn-text-danger" onClick={onDiscardAll}>
              Discard all ({sessions.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
